import { useEffect, useRef, useState } from "react";

const SCENE_MS = 2300;
const STEP_MS = 260;

function NeedleScene() {
  return (
    <div className="needle-scene" data-testid="needle-scene">
      <svg viewBox="0 0 320 260" className="w-[min(460px,86vw)] overflow-visible">
        <rect x="95" y="120" width="130" height="86" rx="6" fill="#000" stroke="#fff" strokeWidth="3" />
        <rect className="ns-screen" x="107" y="131" width="106" height="62" rx="3" fill="#000" />
        <rect x="153" y="206" width="14" height="16" fill="#000" stroke="#fff" strokeWidth="3" />
        <rect x="127" y="222" width="66" height="7" rx="3.5" fill="#000" stroke="#fff" strokeWidth="3" />
        <rect className="ns-stream" x="158.5" y="104" width="3" height="17" fill="#3dff73" />
        <g className="ns-syringe">
          <rect x="147" y="6" width="26" height="66" rx="5" fill="#000" stroke="#fff" strokeWidth="3" />
          <rect className="ns-liquid" x="152" y="14" width="16" height="50" fill="#3dff73" />
          <rect x="158.5" y="72" width="3" height="34" fill="#fff" />
          <g className="ns-plunger">
            <rect x="157" y="-14" width="6" height="20" fill="#fff" />
            <rect x="148" y="-24" width="24" height="10" rx="3" fill="#fff" />
          </g>
        </g>
        <ellipse className="ns-pool" cx="160" cy="232" rx="105" ry="14" fill="#3dff73" opacity="0.9" />
        <circle className="ns-drip1" cx="130" cy="230" r="4" fill="#3dff73" />
        <circle className="ns-drip2" cx="192" cy="232" r="5" fill="#3dff73" />
      </svg>
      <div className="ns-caption font-mono text-[10px] tracking-[0.35em] text-white/50">{"// DIRECT INJECTION"}</div>
    </div>
  );
}

function MatrixScene() {
  return (
    <div className="matrix-scene" data-testid="matrix-scene">
      {Array.from({ length: 13 }).map((_, i) => (
        <span key={i} className="matrix-col" style={{ animationDelay: `${i * 0.11}s` }}>
          {Array.from({ length: 16 }).map((__, j) => (
            <i key={j}>{["0", "1", "X", "#", "A", "F", "7", "$"][(i + j) % 8]}</i>
          ))}
        </span>
      ))}
      <div className="matrix-core font-mono">
        <div className="text-[10px] tracking-[0.35em] text-emerald-200">KERNEL MEMORY BYPASS</div>
        <div className="mt-3 font-display text-4xl tracking-[0.12em]">0x7FFF LOCKED</div>
      </div>
    </div>
  );
}

function RadarScene() {
  return (
    <div className="radar-scene" data-testid="radar-scene">
      <div className="radar-dish">
        <span className="radar-ring r1" />
        <span className="radar-ring r2" />
        <span className="radar-ring r3" />
        <span className="radar-sweep" />
        <span className="radar-blip b1" />
        <span className="radar-blip b2" />
        <span className="radar-center" />
      </div>
      <div className="mt-8 text-center">
        <div className="font-mono text-[10px] tracking-[0.35em] text-emerald-200">ORBITAL ANTICHEAT PING</div>
        <div className="mt-2 font-mono text-xs tracking-[0.2em] text-white/55">THREATS FOUND: 00</div>
      </div>
    </div>
  );
}

const VARIANTS = {
  needle: {
    scene: <NeedleScene />,
    lines: [
      "GZ SECURE LOADER v4.2.1",
      "> establishing uplink ............ OK",
      "> bypass chain [3/3] ............. OK",
      "> integrity check ................ PASS",
      "> injecting payload .............. DONE",
    ],
  },
  cyber_matrix: {
    scene: <MatrixScene />,
    lines: [
      "GZ KERNEL BRIDGE v4.2.1",
      "> mapping memory offsets ......... OK",
      "> bypass chain [3/3] ............. OK",
      "> spoof signature ................ PASS",
      "> payload handoff ................ DONE",
    ],
  },
  radar_scan: {
    scene: <RadarScene />,
    lines: [
      "GZ ORBITAL SCAN v4.2.1",
      "> pinging anticheat grid ......... OK",
      "> threats found: 00 .............. OK",
      "> clearing bypass flags .......... PASS",
      "> injection corridor open ........ DONE",
    ],
  },
};

export default function BootSequence() {
  const [show, setShow] = useState(() => {
    const forced = new URLSearchParams(window.location.search).has("bootscene");
    try { return forced || !sessionStorage.getItem("inj_boot_seen"); } catch { return true; }
  });
  const [variant] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("bootscene");
    if (q && VARIANTS[q]) return q;
    const keys = Object.keys(VARIANTS);
    return keys[Math.floor(Math.random() * keys.length)];
  });
  const [phase, setPhase] = useState("scene");
  const [step, setStep] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const timers = useRef([]);
  const lines = VARIANTS[variant].lines;

  useEffect(() => {
    if (!show) return undefined;
    document.body.style.overflow = "hidden";
    if (new URLSearchParams(window.location.search).has("bootscene")) return undefined;
    timers.current = [
      setTimeout(() => setPhase("terminal"), SCENE_MS),
      ...lines.map((_, i) => setTimeout(() => setStep(i + 1), SCENE_MS + STEP_MS * (i + 1))),
      setTimeout(finish, SCENE_MS + STEP_MS * lines.length + 700),
    ];
    return () => {
      timers.current.forEach(clearTimeout);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = () => {
    try { sessionStorage.setItem("inj_boot_seen", "1"); } catch { /* private mode */ }
    setLeaving(true);
    setTimeout(() => { setShow(false); document.body.style.overflow = ""; }, 420);
  };

  if (!show) return null;

  return (
    <div
      data-testid="boot-sequence"
      onClick={finish}
      className={`fixed inset-0 z-[200] bg-black flex items-center justify-center cursor-pointer transition-opacity duration-500 ${leaving ? "opacity-0 pointer-events-none" : "opacity-100"}`}
    >
      <span className="sr-only" data-testid={`boot-variant-${variant}`}>{variant}</span>
      {phase === "scene" ? (
        VARIANTS[variant].scene
      ) : (
        <div className="w-[min(560px,90vw)] font-mono text-sm sm:text-base">
          {lines.slice(0, step).map((l, i) => (
            <div key={i} className={`leading-loose tracking-[0.08em] ${i === 0 ? "text-white" : "text-white/55"}`}>
              {l}
            </div>
          ))}
          <div className="mt-6 h-px bg-white/15 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-white transition-[width] duration-300" style={{ width: `${(step / lines.length) * 100}%` }} />
          </div>
          {step >= lines.length && (
            <div className="mt-8 text-center font-display text-2xl sm:text-3xl tracking-[0.25em] text-white animate-pulse">
              {"// EXECUTE // INJECT // DOMINATE"}
            </div>
          )}
        </div>
      )}
      <div className="absolute bottom-10 left-0 right-0 text-center text-[10px] tracking-[0.3em] text-white/25 font-mono">CLICK TO SKIP</div>
    </div>
  );
}
